import { select, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { StyledLink } from 'components/link/styles';
import { useCurrentPharmacyOrThrow } from 'hooks/slices';
import type { Panelist } from 'types/models/pharmacy_lgo_settings';
import { ospharmWelcomeLink } from 'utils/linkUtils';
import { PANELIST_SUPPORT_PHONE_NUMBER } from '../../../constants';

type Props = {
  panelist: Panelist;
  onboardingUrl: string | null;
};

export const PanelistConnectionSteps = ({ panelist, onboardingUrl }: Props) => {
  const currentPharmacy = useCurrentPharmacyOrThrow();

  return (
    <>
      {t`Pour que la connexion soit opérationnelle :`}
      <ul style={{ marginLeft: 20 }}>
        <li>
          <Trans>
            Assurez-vous que vous avez bien un <strong>contrat</strong> avec l’extracteur{' '}
            {select(panelist, {
              santestat: 'Santestat',
              ospharm: 'Ospharm',
              offisante: 'Offisanté',
              other: panelist,
            })}
            .
          </Trans>
          {panelist === 'offisante' && onboardingUrl && (
            <>
              {' '}
              <StyledLink href={onboardingUrl} target="_blank">
                {t`Cliquer ici pour adhérer à Offisanté et permettre la transmission de vos données de vente à Faks.`}
              </StyledLink>
            </>
          )}
        </li>

        {panelist === 'santestat' && (
          <li>
            <Trans>
              Assurez-vous que vous avez autorisé Santestat à transmettre vos données de vente à
              Faks.{' '}
              <strong>
                Santestat doit vous envoyer un email avec l’objet suivant « SANTESTAT - votre
                contrat » et contenant un lien web permettant de donner votre accord.
              </strong>
            </Trans>
          </li>
        )}

        {panelist === 'ospharm' && (
          <li>
            <Trans>
              Assurez-vous que vous avez autorisé Ospharm à transmettre vos données de vente à Faks.
            </Trans>{' '}
            <StyledLink href={ospharmWelcomeLink(currentPharmacy.cip)} target="_blank">
              {t`Cliquer ici pour autoriser Ospharm à transmettre vos données de vente à Faks.`}
            </StyledLink>
          </li>
        )}

        {panelist === 'offisante' && (
          <li>
            <Trans>
              La connexion entre Faks et Offisanté sera{' '}
              <strong>opérationnelle en quelques jours</strong>.
            </Trans>
          </li>
        )}

        {panelist === 'santestat' && (
          <li>
            <Trans>
              La connexion entre Faks et Santestat sera opérationnelle{' '}
              <strong>quelques jours après la signature de votre accord de partage</strong>{' '}
              effectuée auprès de Santestat.
            </Trans>
          </li>
        )}

        {panelist === 'ospharm' && (
          <li>
            <Trans>
              La connexion entre Faks et Ospharm sera opérationnelle{' '}
              <strong>quelques jours après la signature de votre accord de partage</strong>{' '}
              effectuée auprès d’Ospharm.
            </Trans>
          </li>
        )}

        <li>
          <Trans>
            La connexion à l’extracteur utilise le CIP pour identifier la pharmacie. Assurez-vous
            que le <strong>CIP {currentPharmacy.cip}</strong> renseigné sur Faks correspond à celui
            enregistré chez l’extracteur.
          </Trans>{' '}
          {panelist === 'offisante' ? (
            <Trans>
              En cas de modification du CIP sur Faks, le statut de connexion sera réinitialisé.
            </Trans>
          ) : (
            <Trans>
              En cas de modification du CIP sur Faks, le statut de connexion sera réinitialisé, ce
              qui pourrait nécessiter une nouvelle signature de l’accord de partage des données.
            </Trans>
          )}
        </li>

        <li>
          {panelist === 'santestat' ? (
            <Trans>
              Contacter le support Santestat au{' '}
              <strong>
                {PANELIST_SUPPORT_PHONE_NUMBER[panelist]} si vous ne recevez pas cet email dans les
                3 semaines qui suivent votre demande de connexion
              </strong>{' '}
              ou pour toute autre question sur la mise en place de cette connexion.
            </Trans>
          ) : (
            <Trans>
              Contacter le support{' '}
              {select(panelist, { ospharm: 'Ospharm', offisante: 'Offisanté', other: panelist })} au{' '}
              <strong>{PANELIST_SUPPORT_PHONE_NUMBER[panelist]}</strong> si vous avez des questions
              sur la mise en place de cette connexion.
            </Trans>
          )}
        </li>
      </ul>
    </>
  );
};
